/**
 * PORTED (REWRITTEN) — RO-3-00d / #44471.
 * Origin: `excellent/app/projects/[projectSlug]/[sectionSlug]/_crm/sequences.tsx`.
 *
 * List enrollment counts moved behind `crm.views.sequences` (workspace-scoped).
 * Detail reuses `crm.sequences.get` + co-ported SequenceEditorClient.
 */

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  EmptyState,
  PageHeader,
  StatusBadge,
  formatDateTime,
  objectHref,
  MaybeObjectLink,
  type DataTableColumn,
  type SequenceEnrollmentStatus,
} from "@excellent/ui";
import { Plus } from "@excellent/ui/icons";
import { RouterLink } from "@excellent/ui/router";
import { useApi } from "@excellent/ui/api";
import { crm } from "@excellent/ui/api/contracts/crm";
import { NewSequenceClient, SequenceEditorClient } from "./client";

interface SeqRow {
  id: number;
  uid: string;
  name: string;
  description: string | null;
  enrollments_active: number;
  enrollments_total: number;
}

export function SequencesView({ basePath }: { basePath: string }) {
  const scoped = !basePath.startsWith("/projects/sections");
  const { data, error, isLoading } = useApi(crm.views.sequences, {});
  const rows = (data?.sequences ?? []) as SeqRow[];

  const columns: DataTableColumn<SeqRow>[] = [
    {
      key: "name",
      header: "Name",
      render: (s) => (
        <MaybeObjectLink
          href={scoped ? `${basePath}/sequences/${s.id}` : objectHref(s.uid, "customers")}
          className="text-foreground hover:underline"
        >
          {s.name}
        </MaybeObjectLink>
      ),
    },
    {
      key: "description",
      header: "Description",
      cellClassName: "text-muted-foreground",
      render: (s) => s.description ?? "",
    },
    {
      key: "active",
      header: "Active",
      width: "w-32",
      cellClassName: "text-muted-foreground tabular-nums",
      render: (s) => s.enrollments_active,
    },
    {
      key: "enrolled",
      header: "Enrolled",
      width: "w-32",
      cellClassName: "text-muted-foreground tabular-nums",
      render: (s) => s.enrollments_total,
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Sequences"
        description="Automated multi-step outreach — enroll leads and each step sends on cadence."
        toolbar={
          scoped ? (
            <Button asChild size="icon-tb">
              <RouterLink
                href={`${basePath}/sequences/new`}
                aria-label="New sequence"
                title="New sequence"
              >
                <Plus className="size-4" />
              </RouterLink>
            </Button>
          ) : undefined
        }
      />
      {error ? (
        <p className="type-body text-destructive">{error.message ?? String(error)}</p>
      ) : null}
      {isLoading && !data ? (
        <p className="type-body text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No sequences yet"
          description="Create an outbound cadence, then add day-offset steps and enroll leads."
          action={
            scoped ? (
              <Button asChild size="icon-tb">
                <RouterLink
                  href={`${basePath}/sequences/new`}
                  aria-label="New sequence"
                  title="New sequence"
                >
                  <Plus className="size-4" />
                </RouterLink>
              </Button>
            ) : undefined
          }
        />
      ) : (
        <DataTable rows={rows} columns={columns} rowKey={(s) => s.id} />
      )}
    </div>
  );
}

export function NewSequenceView({ basePath }: { basePath: string }) {
  return (
    <div className="mx-auto flex max-w-xl flex-col gap-4">
      <PageHeader
        title="New sequence"
        description="Name the sequence, then add its first step."
      />
      <NewSequenceClient basePath={basePath} />
    </div>
  );
}

export function SequenceDetailView({
  basePath: _basePath,
  id,
}: {
  basePath: string;
  id: string;
}) {
  const seqId = Number(id);
  const { data, error, isLoading } = useApi(crm.sequences.get, {
    params: { id: seqId },
  });
  const { data: templatesData } = useApi(crm.templates.list, {});

  if (isLoading && !data) {
    return <p className="type-body text-muted-foreground">Loading…</p>;
  }
  if (error || !data?.sequence) {
    return (
      <EmptyState
        title="Sequence not found"
        description={error?.message ?? "This sequence may have been archived."}
      />
    );
  }

  const { sequence, steps, enrollments } = data;
  const templates = templatesData?.templates ?? [];

  return (
    <div className="flex flex-col gap-4">
      <SequenceEditorClient
        sequence={{
          id: sequence.id,
          name: sequence.name,
          description: sequence.description,
          paused: sequence.paused,
        }}
        steps={steps.map((s) => ({
          id: s.id,
          sort_key: s.sort_key,
          day_offset: s.day_offset,
          channel: s.channel,
          template_id: s.template_id,
          instructions: s.instructions,
        }))}
        templates={templates.map((t) => ({ id: t.id, name: t.name }))}
      />
      <Card>
        <CardHeader>
          <CardTitle>Enrollments ({enrollments.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {enrollments.length === 0 ? (
            <EmptyState
              variant="inline"
              title="No leads enrolled"
              description="Leads you enroll in this sequence appear here, with their next scheduled step."
            />
          ) : (
            <ul className="flex flex-col gap-1 type-body">
              {enrollments.map((e) => (
                <li
                  key={e.id}
                  className="flex items-center gap-2 border-b border-border/40 py-1 last:border-b-0"
                >
                  <span className="text-foreground">lead #{e.lead_id}</span>
                  <StatusBadge
                    domain="sequence_enrollment"
                    status={e.status as SequenceEnrollmentStatus}
                  />
                  {e.next_action_at && (
                    <span className="ml-auto text-muted-foreground">
                      next: {formatDateTime(e.next_action_at)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
